/**
 * MOD-01b-3 (Betreiberfreigabe 13.10.) – Abschnitt „Zusatzmodule" in Tarif & Abrechnung (nur Owner).
 *
 * Daten ausschließlich aus GET /api/billing/modules; Aktionen nur über die Backend-Endpunkte
 * book / cancel / undo-cancel (kein Stripe im Frontend, keine Preisermittlung im Frontend).
 * Alle Bildschirmtexte wortgleich aus dem Auftrag; die API-message wird nur bei Fehlern (Soll 6)
 * angezeigt. Nach jeder Aktion wird die Übersicht neu geladen (keine lokale Annahme über den
 * Zustand). Daten TT.MM.JJJJ in UTC; Zeitraum-Enden (Periodengrenze) als Tag davor
 * (FIX-ABR-01, Betreiberfreigabe 22.10.2026, wie Rechnungstexte). Beträge deutsch mit Komma.
 *
 * KAUF-FIX-01 (Betreiberfreigabe 21.10., Texte V4): Zubuchung wird vor der Freischaltung bezahlt –
 * Dialogtext „aktiv, sobald die Zahlung bestätigt ist", Zustand „Zahlung offen" (SEPA, keine Aktion),
 * Hinweis bei abgelehnter Zahlung (erneut buchbar); Fehlermeldung des Backends (402) wie Soll 6.
 * SAMMEL-A3 (Betreiberfreigabe 22.10.): Dialogtext ohne Sofortrechnung – sofort freigeschaltet, Einzug mit der nächsten
 * Monatsrechnung (Soll 1/2); Zustands-Hinweise für Altbuchungen unverändert.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export const ZUSATZMODULE_HINWEIS =
  "Je 1,00 € netto pro gezählter Einheit und Monat, monatlich kündbar. " +
  "Gezählt wird jede Einheit, die im Abrechnungszeitraum mindestens einen Tag aktiv war.";

// Soll 2: Reihenfolge, Beschreibung und Einheit je Modul (wortgleich).
export const MODUL_REIHENFOLGE = ["mehrsprachigkeit", "fuehrerschein", "aushang_addon"];
export const MODUL_TEXTE = {
  mehrsprachigkeit: {
    beschreibung: "Unterweisungen in der eingestellten Sprache Ihrer Mitarbeiter.",
    einheit: "Mitarbeiter mit Fremdsprache",
  },
  fuehrerschein: {
    beschreibung: "Regelmäßige Führerscheinkontrolle Ihrer Fahrer.",
    einheit: "Fahrer",
  },
  aushang_addon: {
    beschreibung: "Aushangpflichtige Gesetze für Ihre Mitarbeiter in der App.",
    einheit: "Mitarbeiter",
  },
};

const STANDARD_FEHLER = "Das hat nicht geklappt. Bitte versuchen Sie es erneut.";
const SOFORTRECHNUNG_FEHLER =
  "Die Zahlung für diese Zubuchung ist fehlgeschlagen. Bitte schreiben Sie uns an info@clartext.io.";
// KAUF-FIX-01 (Betreiberfreigabe 21.10., Texte V4): Zahlung vor Freischaltung.
export const ZAHLUNG_ABGELEHNT_HINWEIS =
  "Die Zahlung für diese Zubuchung wurde abgelehnt. Bitte prüfen Sie Ihre Zahlungsart und buchen Sie das Modul erneut.";
export const ZAHLUNG_OFFEN_ZUSATZ =
  "Ihre Zahlung wird noch bestätigt. Das Modul wird danach automatisch freigeschaltet.";
// SAMMEL-A3, Betreiberfreigabe 22.10.: AKTIV_NACH_ZAHLUNG entfällt (keine Sofortrechnung, Soll 1).
const NACHSATZ =
  "Danach wird das Modul monatlich mit Ihrem Abo abgerechnet. " +
  "Abbestellen ist jederzeit zum Ende des laufenden Abrechnungszeitraums möglich.";
const ERSTATTUNG = "Eine anteilige Erstattung erfolgt nicht.";

// Soll 2: Zustand (aus API) → Statusanzeige, Zusatz, Aktion.
const STATUS = {
  testphase: { text: "In der Testphase frei", zusatz: "Nach Abschluss des Abos einzeln buchbar." },
  kein_abo: { text: "Nicht gebucht", zusatz: "Buchbar mit aktivem Abo." },
  anfrage: {
    text: "Nicht gebucht",
    zusatz:
      "Für Ihren Vertrag schalten wir Zusatzmodule auf Anfrage frei. Bitte schreiben Sie uns an info@clartext.io.",
  },
  buchbar: { text: "Nicht gebucht", aktion: "buchen", button: "Hinzubuchen" },
  gebucht: { text: "Gebucht", aktion: "abbestellen", button: "Abbestellen" },
  abbestellt: {
    text: (m) => `Abbestellt zum ${fmtTag(m.cancel_effective_at, { ende: true })}`,
    aktion: "zuruecknehmen",
    button: "Abbestellung zurücknehmen",
  },
  // KAUF-FIX-01 (D2): SEPA-Einzug läuft – keine Aktion, Freischaltung automatisch nach Zahlungseingang.
  zahlung_offen: { text: "Zahlung offen", zusatz: ZAHLUNG_OFFEN_ZUSATZ },
  freigeschaltet: { text: "Freigeschaltet" },
  nicht_buchbar: { text: "Derzeit nicht buchbar" },
};

export function zustandVon(m) {
  if (m.abbestellbar) return "gebucht";
  if (m.rueckholbar) return "abbestellt";
  if (m.buchbar) return "buchbar";
  // MOD-01b-4 (Betreiberfreigabe 13.10., Soll 5): „Buchbar mit aktivem Abo." nur für kein_abo,
  // lesezugriff, kein_zugang; nicht ausdrücklich zugeordnete grund-Werte → „Derzeit nicht buchbar".
  switch (m.grund) {
    case "testphase":
      return "testphase";
    case "modul_bereits_aktiv":
      return "freigeschaltet";
    case "ausserhalb_geltungsbereich":
      return "anfrage";
    case "zahlung_offen":
      return "zahlung_offen";
    case "kein_abo":
    case "lesezugriff":
    case "kein_zugang":
      return "kein_abo";
    default:
      return "nicht_buchbar";
  }
}

const pad = (n) => String(n).padStart(2, "0");

// TT.MM.JJJJ (UTC); `ende` = Periodengrenze → Tag davor (FIX-ABR-01, Betreiberfreigabe 22.10.2026).
export function fmtTag(iso, { ende = false } = {}) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const d = new Date(t);
  if (ende) d.setUTCDate(d.getUTCDate() - 1); // FIX-ABR-01, Betreiberfreigabe 22.10.2026 (S1)
  return `${pad(d.getUTCDate())}.${pad(d.getUTCMonth() + 1)}.${d.getUTCFullYear()}`;
}

function fmtBetrag(cents) {
  return (
    (cents / 100).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €"
  );
}

function einheitVon(m) {
  return (MODUL_TEXTE[m.module_id] || {}).einheit || "";
}

// Soll 3: Dialogtext „Hinzubuchen" (n > 0 mit/ohne Zeitraum, n = 0).
// SAMMEL-A3, Betreiberfreigabe 22.10.: sofort freigeschaltet, Einzug mit der nächsten Monatsrechnung, keine Sofortrechnung.
export function buchenText(m) {
  const n = Number(m.einheiten) || 0;
  if (n === 0) {
    // SAMMEL-A3, Betreiberfreigabe 22.10. (Soll 2)
    return `${m.name} wird sofort freigeschaltet. Derzeit sind 0 ${einheitVon(m)} erfasst, daher fällt jetzt nichts an. Kommt ein Mitarbeiter dazu, wird er mit der nächsten Monatsrechnung berechnet. ${NACHSATZ}`;
  }
  const preis = m.preis_netto_cents_je_einheit == null ? 100 : m.preis_netto_cents_je_einheit;
  const z = m.abrechnungszeitraum || null;
  const von = fmtTag(z && z.von);
  const bis = fmtTag(z && z.bis, { ende: true });
  const zeitraum = von && bis ? `${von}–${bis}: ` : ""; // SAMMEL-A3, Betreiberfreigabe 22.10.: ohne Zeitraum ohne Datum
  return (
    // SAMMEL-A3, Betreiberfreigabe 22.10. (Soll 1)
    `${m.name} wird sofort freigeschaltet. Den vollen Monatsbetrag für den laufenden Monat ` +
    `(${zeitraum}${n} × ${fmtBetrag(preis)} = ${fmtBetrag(n * preis)} netto zzgl. 19 % USt.) ` +
    `berechnen wir mit Ihrer nächsten Monatsrechnung. Es gibt keine Sofortrechnung. ${NACHSATZ}`
  );
}

// Soll 4: Dialogtext „Abbestellen" (mit/ohne gespeicherten Zeitraum).
export function abbestellenText(m) {
  const bis = fmtTag(m.abrechnungszeitraum && m.abrechnungszeitraum.bis, { ende: true });
  return bis
    ? `Das Modul bleibt bis zum ${bis} aktiv und wird danach nicht mehr berechnet. ${ERSTATTUNG}`
    : `Das Modul bleibt bis zum Ende des laufenden Abrechnungszeitraums aktiv und wird danach nicht mehr berechnet. ${ERSTATTUNG}`;
}

export function erfolgBuchen(m, antwort) {
  // KAUF-FIX-01 (D2): SEPA – Zahlung läuft, Freischaltung automatisch nach Zahlungseingang.
  if (antwort && antwort.status === "zahlung_offen") {
    return `${m.name}: ${ZAHLUNG_OFFEN_ZUSATZ}`;
  }
  const basis = `${m.name} ist gebucht.`;
  return m.module_id === "mehrsprachigkeit"
    ? `${basis} Ihre freigegebenen Welten werden jetzt in die Sprachen Ihrer Mitarbeiter übersetzt. Das kann einige Minuten dauern.`
    : basis;
}

function erfolgAbbestellen(m, antwort) {
  const datum = fmtTag(antwort && antwort.cancel_effective_at, { ende: true });
  return datum
    ? `${m.name} ist zum ${datum} abbestellt.`
    : `${m.name} ist zum Ende des laufenden Abrechnungszeitraums abbestellt.`;
}

const erfolgZurueck = (m) => `Abbestellung zurückgenommen. ${m.name} bleibt gebucht.`;

// Soll 6: deutsche message aus der API, sonst Standardtext.
export function fehlerText(e) {
  const msg = e && e.response && e.response.data && e.response.data.detail && e.response.data.detail.message;
  return typeof msg === "string" && msg.trim() ? msg : STANDARD_FEHLER;
}

function ModulKarte({ m, busy, onAktion }) {
  const texte = MODUL_TEXTE[m.module_id] || { beschreibung: "", einheit: "" };
  const zustand = zustandVon(m);
  const status = STATUS[zustand];
  const statusText = typeof status.text === "function" ? status.text(m) : status.text;
  const id = `zusatzmodul-${m.module_id}`;
  return (
    <div className="rounded-lg border border-slate-200 p-4 space-y-2" data-testid={id} data-zustand={zustand}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex-1 min-w-[200px]">
          <div className="font-semibold text-slate-900" data-testid={`${id}-name`}>
            {m.name}
          </div>
          <p className="text-sm text-slate-600" data-testid={`${id}-beschreibung`}>
            {texte.beschreibung}
          </p>
          <p className="text-sm text-slate-700 mt-1" data-testid={`${id}-einheiten`}>
            Derzeit {m.einheiten} {texte.einheit}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="text-sm font-medium text-slate-900" data-testid={`${id}-status`}>
            {statusText}
          </div>
          {status.aktion && (
            <Button
              size="sm"
              variant={status.aktion === "buchen" ? "default" : "outline"}
              disabled={!!busy}
              onClick={() => onAktion(status.aktion, m)}
              data-testid={`${id}-aktion`}
              data-aktion={status.aktion}
            >
              {status.button}
            </Button>
          )}
        </div>
      </div>
      {status.zusatz && (
        <p className="text-xs text-slate-500" data-testid={`${id}-zusatz`}>
          {status.zusatz}
        </p>
      )}
      {m.gebucht && m.sofortrechnung && m.sofortrechnung.status === "fehlgeschlagen" && (
        <p
          className="text-sm text-red-900 bg-red-50 border border-red-200 rounded-md px-3 py-2"
          data-testid={`${id}-sofortrechnung-hinweis`}
        >
          {SOFORTRECHNUNG_FEHLER}
        </p>
      )}
      {m.buchung_status === "abgelehnt" && (
        <p
          className="text-sm text-red-900 bg-red-50 border border-red-200 rounded-md px-3 py-2"
          data-testid={`${id}-zahlung-abgelehnt-hinweis`}
        >
          {ZAHLUNG_ABGELEHNT_HINWEIS}
        </p>
      )}
    </div>
  );
}

function ZusatzmodulDialog({ dialog, busy, onBestaetigen, onSchliessen }) {
  const m = dialog ? dialog.modul : null;
  const buchen = !!dialog && dialog.art === "buchen";
  return (
    <Dialog open={!!dialog} onOpenChange={(o) => !o && !busy && onSchliessen()}>
      <DialogContent data-testid="zusatzmodul-dialog" data-art={dialog ? dialog.art : ""}>
        {m && (
          <>
            <DialogHeader>
              <DialogTitle data-testid="zusatzmodul-dialog-titel">
                {buchen ? `${m.name} hinzubuchen` : `${m.name} abbestellen`}
              </DialogTitle>
              <DialogDescription data-testid="zusatzmodul-dialog-text">
                {buchen ? buchenText(m) : abbestellenText(m)}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                disabled={!!busy}
                onClick={onSchliessen}
                data-testid="zusatzmodul-dialog-abbrechen"
              >
                {buchen ? "Abbrechen" : "Behalten"}
              </Button>
              <Button
                variant={buchen ? "default" : "destructive"}
                disabled={!!busy}
                onClick={onBestaetigen}
                data-testid="zusatzmodul-dialog-bestaetigen"
              >
                {buchen ? "Kostenpflichtig hinzubuchen" : "Abbestellen"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ZusatzmoduleAbschnitt() {
  const [module, setModule] = useState(null);
  const [ladeFehler, setLadeFehler] = useState(false);
  const [meldung, setMeldung] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(null);

  const laden = useCallback(() => {
    api
      .getBillingModules()
      .then((d) => {
        setModule(d && Array.isArray(d.modules) ? d.modules : []);
        setLadeFehler(false);
      })
      .catch(() => setLadeFehler(true));
  }, []);

  useEffect(() => {
    laden();
  }, [laden]);

  // Soll 7: Anker #zusatzmodule – Ziel des Sprach-Hinweises.
  useEffect(() => {
    if (module && typeof window !== "undefined" && window.location.hash === "#zusatzmodule") {
      const el = document.getElementById("zusatzmodule");
      if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "start" });
    }
  }, [module]);

  const ausfuehren = async (m, aufruf, erfolg) => {
    if (busy) return;
    setBusy(m.module_id);
    setMeldung(null);
    try {
      const antwort = await aufruf(m.module_id);
      const text = erfolg(antwort);
      setMeldung({ art: "erfolg", text });
      toast.success(text);
    } catch (e) {
      const text = fehlerText(e);
      setMeldung({ art: "fehler", text });
      toast.error(text);
    } finally {
      setDialog(null);
      setBusy(null);
      laden();
    }
  };

  const onAktion = (aktion, m) => {
    if (aktion === "buchen" || aktion === "abbestellen") {
      setDialog({ art: aktion, modul: m });
      return;
    }
    ausfuehren(m, api.undoCancelModule, () => erfolgZurueck(m));
  };

  const bestaetigen = () => {
    if (!dialog) return;
    const m = dialog.modul;
    if (dialog.art === "buchen") ausfuehren(m, api.bookModule, (antwort) => erfolgBuchen(m, antwort));
    else ausfuehren(m, api.cancelModule, (antwort) => erfolgAbbestellen(m, antwort));
  };

  const sortiert = (module || [])
    .slice()
    .sort((a, b) => {
      const ia = MODUL_REIHENFOLGE.indexOf(a.module_id);
      const ib = MODUL_REIHENFOLGE.indexOf(b.module_id);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });

  return (
    <section
      id="zusatzmodule"
      className="border-t border-slate-100 pt-4 space-y-3"
      data-testid="zusatzmodule-abschnitt"
    >
      <h3 className="font-heading text-base font-semibold text-slate-900">Zusatzmodule</h3>
      <p className="text-sm text-slate-600" data-testid="zusatzmodule-hinweis">
        {ZUSATZMODULE_HINWEIS}
      </p>
      {meldung && (
        <div
          role={meldung.art === "erfolg" ? "status" : "alert"}
          className={
            meldung.art === "erfolg"
              ? "rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900"
              : "rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900"
          }
          data-testid="zusatzmodule-meldung"
          data-art={meldung.art}
        >
          {meldung.text}
        </div>
      )}
      {ladeFehler && (
        <p
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900"
          data-testid="zusatzmodule-ladefehler"
        >
          {STANDARD_FEHLER}
        </p>
      )}
      {module && (
        <div className="space-y-3" data-testid="zusatzmodule-liste">
          {sortiert.map((m) => (
            <ModulKarte key={m.module_id} m={m} busy={busy} onAktion={onAktion} />
          ))}
        </div>
      )}
      <ZusatzmodulDialog
        dialog={dialog}
        busy={busy}
        onBestaetigen={bestaetigen}
        onSchliessen={() => setDialog(null)}
      />
    </section>
  );
}
